// src/components/chat/ChatWindow.jsx - ДОБАВЛЕН ИНДИКАТОР НЕАКТИВНОСТИ
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useSafeTimeout } from '../../hooks/useSafeTimeout';
import { usePageVisibility } from '../../hooks/usePageVisibility';
import { formatters } from '../../utils/formatters';
import { CONSTANTS } from '../../constants';
import ChatMessage from './ChatMessage';
import ChatRoomHeader from './ChatRoomHeader';

const ChatWindow = () => {
  const {
    currentRoom,
    messages,
    loadingMessages,
    loadingMoreMessages,
    sendingMessage,
    error,
    setError,
    hasMoreMessages,
    typingUsers,
    replyTo,
    setReplyTo,
    sendMessageRest,
    loadMoreMessages,
    sendTypingIndicator
  } = useChat();

  const { user } = useAuth();
  const { isConnected } = useWebSocket();
  const { setSafeTimeout, clearSafeTimeout, isMounted } = useSafeTimeout();
  const { isVisible, isHidden } = usePageVisibility();

  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(null);

  const messagesContainerRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const isUserAtBottomRef = useRef(true);
  const lastScrollHeightRef = useRef(0);
  const fileInputRef = useRef(null);

  // Группировка сообщений по датам
  const groupedMessages = useMemo(() => {
    const groups = [];
    let lastDate = null;

    for (const message of messages) {
      const date = new Date(message.created_at).toDateString();

      if (date !== lastDate) {
        groups.push({
          type: 'date',
          date: message.created_at,
          id: `date-${date}-${message.id}`
        });
        lastDate = date;
      }

      groups.push(message);
    }

    return groups;
  }, [messages]);

  // Проверка нахождения внизу
  const checkIfAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 40;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Скролл вниз
  const scrollToBottom = useCallback((instant = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (!isUserAtBottomRef.current) return;

    if (instant) {
      container.scrollTop = container.scrollHeight;
    } else {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Начальная загрузка
  useEffect(() => {
    if (!loadingMessages && messages.length && isFirstLoadRef.current) {
      const container = messagesContainerRef.current;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
      isFirstLoadRef.current = false;
    }
  }, [loadingMessages, messages.length]);

  // Новые сообщения
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const lastMessage = messages[messages.length - 1];
    const isOwnMessage = lastMessage?.user_id === user?.id;

    if (isUserAtBottomRef.current || isOwnMessage) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom, user?.id]);

  // Обработчик скролла
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    isUserAtBottomRef.current = checkIfAtBottom();

    if (
      container.scrollTop < 50 &&
      hasMoreMessages &&
      !loadingMoreMessages &&
      !isLoadingMoreRef.current
    ) {
      const oldHeight = container.scrollHeight;
      isLoadingMoreRef.current = true;

      loadMoreMessages().then(() => {
        const newHeight = container.scrollHeight;
        container.scrollTop += newHeight - oldHeight;
      }).finally(() => {
        isLoadingMoreRef.current = false;
      });
    }
  }, [checkIfAtBottom, hasMoreMessages, loadingMoreMessages, loadMoreMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Обработчик печатания
  const handleTyping = useCallback((typing) => {
    if (!currentRoom || !isConnected) return;
    sendTypingIndicator(currentRoom.id, typing);
  }, [currentRoom, isConnected, sendTypingIndicator]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setMessageText(value);

    if (typingTimeout) clearSafeTimeout(typingTimeout);

    if (value.length) handleTyping(true);

    const timeout = setSafeTimeout(() => {
      handleTyping(false);
      setTypingTimeout(null);
    }, CONSTANTS.TIMING.TYPING_TIMEOUT);

    setTypingTimeout(timeout);
  };

  // Отправка сообщения
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if ((!messageText.trim() && !attachments.length) || !currentRoom || sendingMessage) return;

    const text = messageText.trim();
    setMessageText('');

    if (typingTimeout) {
      clearSafeTimeout(typingTimeout);
      handleTyping(false);
      setTypingTimeout(null);
    }

    await sendMessageRest(currentRoom.id, text, attachments);
    setAttachments([]);
    setReplyTo(null);
  };

  // Обработка Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Обработка файлов
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const valid = files.filter(file => {
      if (file.size > CONSTANTS.LIMITS.FILE_SIZE_MAX) {
        alert(`Файл ${file.name} слишком большой`);
        return false;
      }
      return true;
    });
    setAttachments(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Текст печатающих пользователей
  const typingText = useMemo(() => {
    if (!currentRoom) return '';
    const typing = typingUsers[currentRoom.id];
    if (!typing || !typing.size) return '';

    const names = Array.from(typing.values()).map(t => t.user_name);

    if (names.length === 1) return `${names[0]} печатает...`;
    if (names.length === 2) return `${names[0]} и ${names[1]} печатают...`;
    return `Несколько человек печатают...`;
  }, [typingUsers, currentRoom]);

  // Превью вложений
  const previews = useMemo(() => {
    return attachments.map(file => ({
      file,
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
  }, [attachments]);

  useEffect(() => {
    return () => {
      previews.forEach(p => {
        if (p.url) URL.revokeObjectURL(p.url);
      });
    };
  }, [previews]);

  if (!currentRoom) {
    return (
      <div className="h-100 d-flex align-items-center justify-content-center flex-column">
        {isHidden && (
          <div className="mb-3 text-center">
            <Badge bg="warning" className="p-2">
              <i className="bi bi-eye-slash me-2"></i>
              Вкладка неактивна
            </Badge>
            <p className="text-muted small mt-2">
              Новые сообщения не будут помечены как прочитанные,<br />
              пока вы не вернетесь на эту вкладку
            </p>
          </div>
        )}
        <i class="bi bi-chat display-4 mb-3 opacity-50"></i>
        <p className="text-muted">Выберите чат</p>
      </div>
    );
  }

  return (
    <div className="chat-window h-100 d-flex flex-column">
      {isHidden && (
        <div className="bg-warning bg-opacity-10 p-2 text-center border-bottom">
          <small className="text-warning">
            <i className="bi bi-eye-slash me-2"></i>
            Вкладка неактивна - сообщения не отмечаются как прочитанные
          </small>
        </div>
      )}

      <ChatRoomHeader room={currentRoom} />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div ref={messagesContainerRef} className="flex-grow-1 overflow-auto">
        <div className="p-3">
          {groupedMessages.map(item => {
            if (item.type === 'date') {
              return (
                <div key={item.id} className="text-center my-3">
                  <Badge bg="light" text="dark">
                    {new Date(item.date).toLocaleDateString()}
                  </Badge>
                </div>
              );
            }
            return (
              <ChatMessage
                key={item.id}
                message={item}
                isOwn={item.user_id === user?.id}
                onReply={setReplyTo}
                roomId={currentRoom.id}
              />
            );
          })}
        </div>
      </div>

      {typingText && (
        <div className="p-2 border-top bg-light">
          <small className="text-muted">{typingText}</small>
        </div>
      )}

      <Form onSubmit={handleSendMessage} className="p-3 border-top">
        <div className="d-flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
          />
          <Button
            variant="outline-secondary"
            onClick={() => fileInputRef.current.click()}
          >
            📎
          </Button>

          <Form.Control
            as="textarea"
            rows={1}
            placeholder="Напишите сообщение..."
            value={messageText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
          />

          <Button
            type="submit"
            disabled={sendingMessage || (!messageText.trim() && !attachments.length)}
          >
            {sendingMessage ? <Spinner size="sm" /> : "➤"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChatWindow;